type BookableWindow = { from: string; to: string } | null | undefined;

const minutesOf = (time: string): number => {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(time);
  if (!match) throw new Error('INVALID_RESERVATION_TIME');
  return Number(match[1]) * 60 + Number(match[2]);
};

export const isOvernightBookableWindow = (window: BookableWindow): boolean => {
  try {
    return Boolean(window?.from && window.to && minutesOf(window.from) > minutesOf(window.to));
  } catch {
    return false;
  }
};

const localParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find(part => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
};

// Match complete local date + time, rather than subtracting clock hours across midnight.
// On a DST overlap prefer the occurrence closest to the original booking; reject gaps.
export const buildReservationDateTimeFromLocalInput = (
  baseDate: Date, time: string, timeZone: string, dayOffset = 0
): Date => {
  const minutes = minutesOf(time);
  const { year, month, day } = localParts(baseDate, timeZone);
  const wallTime = Date.UTC(year, month - 1, day + dayOffset, Math.floor(minutes / 60), minutes % 60);
  const offsets = new Set<number>();
  for (const hours of [-36, 0, 36]) {
    const sample = new Date(wallTime + hours * 3_600_000);
    const p = localParts(sample, timeZone);
    offsets.add(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - sample.getTime());
  }
  const matches = [...offsets].map(offset => new Date(wallTime - offset)).filter(candidate => {
    const p = localParts(candidate, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) === wallTime;
  }).sort((a, b) => Math.abs(a.getTime() - baseDate.getTime()) - Math.abs(b.getTime() - baseDate.getTime()));
  if (!matches.length) throw new Error('INVALID_RESERVATION_TIME');
  return matches[0];
};

export const buildGuestModificationInterval = (input: {
  baseDate: Date; startTime: string; endTime: string; timeZone: string; bookableWindow?: BookableWindow;
}): { start: Date; end: Date } => {
  const startMinutes = minutesOf(input.startTime);
  const endMinutes = minutesOf(input.endTime);
  const crossesMidnight = endMinutes < startMinutes && isOvernightBookableWindow(input.bookableWindow);
  const start = buildReservationDateTimeFromLocalInput(input.baseDate, input.startTime, input.timeZone);
  const end = buildReservationDateTimeFromLocalInput(input.baseDate, input.endTime, input.timeZone, crossesMidnight ? 1 : 0);
  if (endMinutes === startMinutes || end.getTime() <= start.getTime()) throw new Error('INVALID_RESERVATION_TIME');
  return { start, end };
};
